/**
 * @fileoverview Ticking the one to-do list (D51), for Today, the top of
 * Plants and the Beds page alike: a job or some of its plants done or
 * undone, a job done today, postponed or skipped, put under When you can,
 * the postponed jobs brought back, and a planting job's day changed. The
 * server does the work and answers as soon as it is saved, with what to say
 * and no brief (B49: building the brief for the answer took past the app's
 * 30 s limit on a slow server, and a saved tick was shown as failed and put
 * back). The brief is then read again on its own and put in place of the
 * page's (useTodayBrief's key), so the mark shows everywhere.
 *
 * The tick shows at once (pending): the circle filled, the plants ticked, or
 * the job struck through as skipped; it stays shown once saved, until a
 * brief asked for after it arrives. A slow or failed read of that brief says
 * nothing and puts nothing back; a failed read is tried once more after
 * TODO_BRIEF_RETRY_MS, and briefs landing out of order never put an older
 * one over a newer (briefApplier). A skip says the server's line under it
 * (said.line) before the brief does. A mark that got no answer at all (a
 * timeout) reads the brief again and counts as saved when the brief shows it
 * (as B43 does for own to-dos). A mark that did not save (a 5xx; no answer
 * and not in the brief; any refusal but 404) says the standard sentence
 * under the job and is reported (D31); a job that has moved on (404) reads
 * the brief again and says so. The decisions are todoMarkFlow's, tested
 * without a screen.
 *
 * Nothing here decides what is done or what is said: the brief that comes
 * back says the list, and the answer's `said` the toast (the to-do rebuild,
 * batch 5): a whole job done, postponed, put under When you can or skipped is
 * said in a toast with Undo in the server's words ("Postponed to tomorrow:
 * Cover tropicals · Undo"; owner, 2026-10-16: "a toast with the undo"), and
 * Undo makes the mark the server names, on the job's key as it now stands
 * (a postponed job's new day). A plant ticked in a job's list has no toast:
 * its own circle is its undo.
 *
 * A Sow outside or Plant out job with Seed vault plants (D69, the brief's planting.askBed; a planting to-do's own.askBed,
 * E23, or the plant card row's own item's askBed, given with the mark) asks "Which bed did they go
 * into?" before the tick (features/seed-orders WhichBedSheet) and sends the bed with it; the server moves them there.
 * Closing the question ticks nothing.
 *
 * A gardener's own to-do (D50) is marked the same way, by its key: ticked,
 * postponed, skipped, and deleted (remove: the
 * edit sheet's Delete, said in a toast with no Undo, B45); sayTodo is the toast the own
 * to-dos' add and edit answers are said with too (features/own-todos).
 *
 * Where: TodayPage (TodoGroups), PlantGrid (PlantJobs), LocationGrid (BedJobs),
 * features/own-todos (the plant and bed cards' "Your to-dos", the sheet).
 * API: POST /todo/marks (api/todo markTodo), then GET /today/brief (api/todayBrief getTodayBrief). The plants and
 * their notes are read again after a tick, as To do's ticks always did; the outlook after a
 * planting job (a plant moved off Planning gets its week), the beds after a
 * garden chore (its bed's journal).
 * Words (en/es): todo.gone (the app's own, for a job that has moved on); every other failure says serverFailure.message.
 *
 * @module useTodoMarks
 */

import { createElement, useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { markTodo, type TodoAsk, type TodoMark, type TodoSaid } from '@/shared/api/todo';
import { getTodayBrief, type TodayBrief, type TodoJob } from '@/shared/api/todayBrief';
import { isServerFailure, reportServerFailure, serverFailureText, serverFailureWords } from '@/shared/lib/serverFailure';
import { toast } from '@/shared/components/ui/use-toast';
import { ToastAction, type ToastActionElement } from '@/shared/components/ui/toast';
import { isNoAnswer } from '@/features/own-todos/ownTodoLanded';
import { PLANT_OUTLOOK_QUERY_KEY } from '@/config/tunables/shared';
import { LOCATIONS_QUERY_KEY } from '@/config/tunables/ui';
import { TODAY_BRIEF_QUERY_KEY, TODO_BRIEF_RETRY_MS } from '@/config/tunables/today';
import { briefApplier, pendingOf, runMark, shownAfterBrief, shownDropped, shownSaved, shownStarted, tickOf, type PendingTick, type Shown } from './todoMarkFlow';
import { askWhichBed, vaultPlantsTicked } from '@/features/seed-orders/lib/whichBed';

/** A tick shown before the brief says it (todoMarkFlow): the job's done or skipped state, or its plants'. */
export type { PendingTick };

/**
 * Says what the server said after a write to the list: its toast, with its Undo when it names one (owner, 2026-10-16:
 * "a toast with the undo"). Nothing when the server says nothing. A removal says its toast with no Undo: once
 * removed, it is removed (owner, 2026-10-20/21, B26/B45); the server's own undo is left alone and not offered.
 *
 * @param said - The answer's said.
 * @param onUndo - Makes the Undo's mark (the action and key the server named).
 * @param removal - The write removed something: its Undo is not offered.
 */
export function sayTodo(said: TodoSaid | null | undefined, onUndo: (undo: NonNullable<TodoSaid['undo']>) => void, removal = false): void {
  if (!said?.toast) return;
  const undo = removal ? null : said.undo ?? null;
  toast({
    title: said.toast,
    action: undo
      ? createElement(ToastAction, { altText: undo.text, onClick: () => onUndo(undo) }, undo.text) as unknown as ToastActionElement
      : undefined,
  });
}

/** What the hook gives a page. */
export interface TodoMarks {
  /**
   * Makes a mark on a job (null: on the postponed jobs as a whole, Bring back, or a mark by its key alone, as an own
   * to-do's Delete). Resolves true once it saved, false when it did not (failed says why). vault: for a mark by key
   * alone, the Seed vault plants its tick moves out (an own to-do row's askBed, the server's; E23).
   */
  mark: (job: TodoJob | null, mark: TodoMark, vault?: readonly number[]) => Promise<boolean>;
  /** The job a mark is on its way for, or null. */
  busyKey: string | null;
  /** The ticks shown before the brief says them, by job key. */
  pending: Record<string, PendingTick>;
  /** The job whose last mark did not save, and what to say under it. */
  failed: { key: string; words: string } | null;
}

/**
 * Ticking, for a page that shows the list.
 *
 * @param queryKey - The key the brief is kept under (useTodayBrief).
 * @param ask - How the brief was asked for (useTodayBrief).
 * @returns The mark and its state.
 */
export function useTodoMarks(queryKey: readonly unknown[], ask: TodoAsk): TodoMarks {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  /** The marks shown before the brief says them (todoMarkFlow), by job key. */
  const [shown, setShown] = useState<Shown>({});
  const pending = useMemo(() => pendingOf(shown), [shown]);
  const [failed, setFailed] = useState<{ key: string; words: string } | null>(null);
  /** The latest mark, for a toast's Undo made by an earlier one. */
  const markRef = useRef<TodoMarks['mark'] | null>(null);
  /** The page's brief, put in place in the order the reads were asked for (todoMarkFlow briefApplier); set below. */
  const applyRef = useRef<((brief: TodayBrief, askedAt: number) => boolean) | null>(null);
  /** The page's key, for the applier made once. */
  const keyRef = useRef(queryKey);
  keyRef.current = queryKey;
  if (!applyRef.current) {
    applyRef.current = briefApplier((brief, askedAt) => {
      queryClient.setQueryData(keyRef.current, brief);
      setShown((current) => shownAfterBrief(current, askedAt));
    });
  }
  const applyBrief = applyRef.current;

  const mark = useCallback(async (job: TodoJob | null, given: TodoMark, vault?: readonly number[]): Promise<boolean> => {
    let todo = given;
    // D69: Seed vault plants on a Sow outside or Plant out job leave the vault: the bed they went into is asked first
    // (the server's planting.askBed names them), and sent with the tick. Closing the question ticks nothing.
    // A planting job's askBed, a planting to-do's (E23: job.own.askBed), or the one a card row gives: each the server's.
    const askBed = job?.planting?.askBed ?? job?.own?.askBed ?? vault;
    if (todo.action === 'done' && todo.bedId === undefined && vaultPlantsTicked(askBed, todo.plantIds).length > 0) {
      const answer = await askWhichBed({ sub: job?.title ?? null });
      if (!answer || !('bedId' in answer)) return false;
      todo = { ...todo, bedId: answer.bedId };
    }
    // A key given with the mark wins over the job's (a toast's Undo names the job's key as it now stands).
    const sentKey = todo.key ?? job?.key;
    const key = sentKey ?? 'moved';
    setBusyKey(key);
    setFailed(null);
    // A tick or a skip shows at once, a job's or one named by its key alone (an own to-do's compact row on a plant's card).
    const tick = tickOf(todo);
    setShown((current) => shownStarted(current, key, tick));
    /** Lets go of the busy state for this mark, unless another mark has taken it since. */
    const notBusy = (): void => setBusyKey((current) => (current === key ? null : current));
    const outcome = await runMark(todo, sentKey, {
      send: () => markTodo({ ...todo, key: sentKey }, ask),
      refresh: () => getTodayBrief(ask.lang, ask.unit, ask.days, ask.device),
      apply: (brief, askedAt) => { applyBrief(brief, askedAt); },
      noAnswer: isNoAnswer,
      now: () => Date.now(),
      wait: (ms) => new Promise((resolve) => { window.setTimeout(resolve, ms); }),
      retryMs: TODO_BRIEF_RETRY_MS,
    });
    if (outcome.result === 'saved') {
      setShown((current) => shownSaved(current, key, outcome.savedAt, outcome.said?.line));
      // The other pages' briefs are stale now (this page's is read again above): marked, not read now.
      void queryClient.invalidateQueries({ queryKey: [TODAY_BRIEF_QUERY_KEY], refetchType: 'none' });
      // Notes and stages changed: the plants (their notes, their stage), the beds' journals, and a plant moved off Planning gets its week.
      void queryClient.invalidateQueries({ queryKey: ['plants'] });
      void queryClient.invalidateQueries({ queryKey: ['notes'] });
      // A planting to-do's tick moves its plant's stage too (E23), from Today or by key alone on the plant's card.
      if (!job || job.kind === 'plant' || job.kind === 'own') void queryClient.invalidateQueries({ queryKey: [PLANT_OUTLOOK_QUERY_KEY] });
      // A garden chore or a bed's own to-do writes in the bed's journal; a mark by key alone may be one.
      if (!job || job.family === 'garden' || todo.bedId !== undefined) void queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
      // The server's words, and the Undo it names, on the job as it now stands.
      sayTodo(outcome.said, (undo) => { void markRef.current?.(job, { action: undo.action, key: undo.key }); }, todo.action === 'remove');
      // A mark that moves the job (a postpone, When you can, back, a day) shows nothing of its own: the job stays busy
      // until the brief read after it has come or failed, so it is not marked again where it no longer is.
      if (tick) notBusy();
      else void outcome.refreshed.finally(notBusy);
      return true;
    }
    const { error } = outcome;
    setShown((current) => shownDropped(current, key));
    notBusy();
    const gone = axios.isAxiosError(error) && error.response?.status === 404;
    if (gone) void queryClient.invalidateQueries({ queryKey: [TODAY_BRIEF_QUERY_KEY] });
    // Any other failure says the standard sentence too (reported, with the support address), never "try again".
    if (!gone && !isServerFailure(error)) reportServerFailure(error, 'To-do mark');
    setFailed({ key, words: serverFailureText(error, 'To-do mark') ?? (gone ? t('todo.gone') : serverFailureWords(error)) });
    return false;
  }, [applyBrief, ask, queryClient, t]);
  markRef.current = mark;

  return { mark, busyKey, pending, failed };
}
